import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { useQuotePromotionCatalog, useRefreshPromotionCatalog } from "../../api/useQuotes";
import { PromotionCatalogRow, QuoteSegment, QuoteTerm } from "../../shared/types";
import "./PromotionsCatalogDialog.scss";

type Props = {
  open: boolean;
  onClose: () => void;
  // Issue 48: restricts which segments this quote's customer can actually be offered - a
  // Commercial-only customer has no business being shown (let alone able to pick) a Charity
  // promotion, even though Microsoft's own catalog has one. Undefined, or every segment, opens
  // unrestricted (a prospect quote, which can be any segment, or a customer eligible for all
  // four - genuinely nothing to narrow in either case). Exactly one segment locks the filter to
  // it outright (no "All Segments", no other options - there is only one valid choice); more than
  // one still offers "All Segments" but narrows the discrete choices to just the eligible ones.
  // "All Segments" itself still queries unfiltered in that multi-eligible case (the catalog API
  // takes one segment at a time, not a list) - onSelect below is the actual, unconditional
  // enforcement regardless of what this narrows the browsing to.
  allowedSegments?: QuoteSegment[];
  // When provided, each row gets a Use action that hands the row back to the caller (and closes
  // the dialog) instead of this being a browse-only view - the quote editor wires this up to add
  // the promotion to the current quote; the read-only list-page entry point leaves it unset.
  onSelect?: (row: PromotionCatalogRow) => void;
  // Set by the caller when onSelect rejected a picked row (e.g. an ineligible segment) - shown
  // inline instead of closing the dialog, so staff immediately see why nothing happened and can
  // just pick a different row without having to reopen this view.
  selectError?: string | null;
  // Microsoft has no per-promotion deep link, so the next best thing: this customer's own
  // Partner Center catalog page, where every promotion actually available to them is listed.
  // Undefined for a prospect quote (no Microsoft customer to link to), in which case the button
  // is omitted rather than shown disabled.
  customerGuid?: string | null;
};

const partnerCenterCatalogUrl = (customerGuid: string) =>
  `https://partner.microsoft.com/dashboard/v2/customers/${customerGuid}/catalog/list?tabViewId=OnlineServices`;

const segments: QuoteSegment[] = ["Commercial", "Charity", "Education", "Government"];
const terms: QuoteTerm[] = ["P1M", "P1YM", "P1YA"];
const rowsPerPageOptions = [25, 50, 100];

const formatDate = (value: string | null) =>
  value ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value)) : "-";

const formatSeatBand = (min: number | null, max: number | null) => {
  if (min == null && max == null) return "Any";
  if (min != null && max != null) return `${min}-${max}`;
  if (min != null) return `${min}+`;
  return `Up to ${max}`;
};

const rowKey = (row: PromotionCatalogRow) => `${row.promo_id}|${row.product_id}|${row.sku_id}|${row.term_duration}`;

const PromotionsCatalogDialog = ({ open, onClose, allowedSegments, onSelect, selectError, customerGuid }: Props) => {
  // Undefined, or every segment, is "nothing to narrow" - the same set as no restriction at all.
  const segmentOptions = allowedSegments && allowedSegments.length > 0 && allowedSegments.length < segments.length
    ? segments.filter(value => allowedSegments.includes(value)) : segments;
  const lockedSegment = segmentOptions.length === 1 ? segmentOptions[0] : null;
  const [segment, setSegment] = useState<QuoteSegment | "">(lockedSegment ?? "");
  const [term, setTerm] = useState<QuoteTerm | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // A row with no locally-synced title can't actually be added to a quote (see includeUntitled
  // on the query) - hidden by default, with this as the escape hatch to see them anyway.
  const [showAll, setShowAll] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Whether this open has already triggered the automatic first-ever-check below, so it fires at
  // most once per open (not on every render, and not again after a failed attempt until reopened).
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (open) { setSegment(lockedSegment ?? ""); setTerm(""); setSearchInput(""); setSearch(""); setPage(0); setShowAll(false); autoRefreshedRef.current = false; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => () => window.clearTimeout(debounceRef.current), []);

  const query = {
    segment: segment || undefined,
    term_duration: term || undefined,
    search: search || undefined,
    page,
    page_size: rowsPerPage,
    include_untitled: showAll || undefined,
  };
  const catalog = useQuotePromotionCatalog(query, open);
  const refresh = useRefreshPromotionCatalog();
  const data = catalog.data;
  // checked_at is the last SUCCESSFUL sync (see PromotionSyncStatusAsync) - null means the store
  // has never actually been checked (a brand-new environment, most commonly), not that a check
  // was attempted and failed. Surfacing "Could not check for updates" for that case is simply
  // false (nothing was ever tried), so trigger one real check automatically instead of silently
  // lying and leaving the user to discover the unrelated manual Refresh button fixes it.
  const neverChecked = data?.checked_at === null;
  useEffect(() => {
    if (open && neverChecked && !autoRefreshedRef.current && !refresh.isLoading) {
      autoRefreshedRef.current = true;
      refresh.mutate(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, neverChecked]);
  const checkingForTheFirstTime = neverChecked && (refresh.isLoading || !autoRefreshedRef.current);

  const changeSearch = (value: string) => {
    setSearchInput(value);
    window.clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(value); setPage(0); }, 300);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" className="promotions-catalog-dialog">
      <DialogTitle className="promotions-catalog-title">
        Current Promotions
        <IconButton aria-label="Close" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {/* DialogContent's own top padding collapses to 0 when it directly follows a DialogTitle
            (MUI's own rule: `.MuiDialogTitle-root + .MuiDialogContent-root { padding-top: 0 }`,
            in DialogContent.js) - with nothing above them, the floating labels on the very first
            row had no room to rise into and were clipped by this scrollable container's own top
            edge. That's the real cause of the label-through-the-border look, confirmed in MUI's
            source - not a notch bug. .promotions-catalog-toolbar's own padding-top below (not an
            override of DialogContent, which would need to out-specificity MUI's rule) fixes it
            directly and unconditionally. */}
        <Box className="promotions-catalog-toolbar">
          <TextField className="promotions-catalog-search" label="Search" size="small" value={searchInput}
            placeholder="Product, SKU, code, discount %, seats, dates, restrictions"
            InputLabelProps={{ shrink: true }}
            onChange={event => changeSearch(event.target.value)} />
          {/* displayEmpty: without it, a Select with value="" renders NO text at all when closed
              (MUI's default), not the "All Segments"/"All Terms" MenuItem text - that blank
              content has near-zero natural width, and this is what was actually squeezing Term
              down to nothing (Segment happened to look fine only because it usually opens with a
              real, wide value like "Commercial" already selected). */}
          {/* "All Segments" is only offered when there's an actual choice among them - with a
              single eligible segment, it would mean the same thing as picking it directly except
              worse: it queries with segment=undefined, which returns every real Microsoft segment
              including the ones this customer isn't eligible for at all. */}
          <TextField select size="small" label="Segment" className="promotions-catalog-filter" value={segment}
            disabled={Boolean(lockedSegment)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
            onChange={event => { setSegment(event.target.value as QuoteSegment | ""); setPage(0); }}>
            {!lockedSegment && <MenuItem value="">All Segments</MenuItem>}
            {segmentOptions.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Term" className="promotions-catalog-filter" value={term}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
            onChange={event => { setTerm(event.target.value as QuoteTerm | ""); setPage(0); }}>
            <MenuItem value="">All Terms</MenuItem>
            {terms.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </TextField>
          {/* Last checked and Refresh are one group (not separate flex siblings) so a wrap always
              keeps them on the same line together, instead of Refresh alone dropping to its own
              row underneath. */}
          <Box className="promotions-catalog-status">
            <Typography variant="caption" color="text.secondary" noWrap>
              Last checked: {data?.checked_at ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(data.checked_at)) : "never"}
            </Typography>
            {/* When the list is empty this same message becomes the central content below instead
                (a small chip plus a separate, wrong "No promotions match these filters" message
                underneath said two different things about the same problem) - and never during
                the automatic first-ever check, which hasn't earned a failure message yet. */}
            {data && !data.sync_ok && !checkingForTheFirstTime && data.items.length > 0 && <Tooltip title="The last check for new promotions did not succeed. This list may be out of date.">
              <Chip size="small" color="warning" icon={<WarningAmberRoundedIcon />} label="Could not check for updates" />
            </Tooltip>}
            <Tooltip title="Actively check Partner Center for the latest promotions.">
              <span>
                <IconButton aria-label="Refresh promotions" disabled={refresh.isLoading} onClick={() => refresh.mutate(query)}>
                  {refresh.isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {refresh.isError && <Alert severity="error" className="promotions-catalog-refresh-error">Refresh failed. Showing the last-known promotions.</Alert>}
        {catalog.isError && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => catalog.refetch()}>Try again</Button>}>Promotions could not be loaded.</Alert>}
        {selectError && <Alert severity="error" className="promotions-catalog-select-error">{selectError}</Alert>}

        <Box className="promotions-catalog-subheader">
          {onSelect && <Typography variant="body2" color="text.secondary" className="promotions-catalog-instructions">
            Click a row to add that promotion to the current quote.
          </Typography>}
          {/* describeChild: a bare Tooltip clones its child with aria-label = the tooltip text,
              which replaces (not supplements) whatever accessible name the child already had -
              here, the Switch's own name from its FormControlLabel text. describeChild uses
              aria-describedby instead, so the checkbox keeps "Show all (including unpriceable
              SKUs)" as its actual name. */}
          <Tooltip describeChild title="A promotion whose SKU isn't in the locally-synced product catalog can't actually be priced or added to a quote yet - hidden by default.">
            <FormControlLabel className="promotions-catalog-show-all" label="Show all (including unpriceable SKUs)"
              control={<Switch size="small" checked={showAll} onChange={event => { setShowAll(event.target.checked); setPage(0); }} />} />
          </Tooltip>
        </Box>

        {catalog.isLoading && !data ? <Box className="promotions-catalog-loading"><CircularProgress size={28} /></Box> : data && (
          <>
            {data.items.length === 0
              ? checkingForTheFirstTime
                ? <Box className="promotions-catalog-loading"><CircularProgress size={20} /><Typography variant="body2">Checking Partner Center for current promotions...</Typography></Box>
                : <Typography className="promotions-catalog-empty" variant="body2" color={data.sync_ok ? "text.secondary" : "error"}>
                    {data.sync_ok ? "No promotions match these filters." : "Could not check for updates."}
                  </Typography>
              : <>
                <TableContainer className="promotions-catalog-table-container">
                  <Table size="small" stickyHeader aria-label="Current promotions">
                    <TableHead>
                      <TableRow>
                        <TableCell>Promotion</TableCell>
                        <TableCell>Offer</TableCell>
                        <TableCell>Segment</TableCell>
                        <TableCell>Term</TableCell>
                        <TableCell align="right">Microsoft Discount</TableCell>
                        <TableCell>Seats</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell>Restrictions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.items.map(row => (
                        <TableRow key={rowKey(row)} hover
                          className={onSelect ? "promotions-catalog-row-selectable" : undefined}
                          onClick={onSelect ? () => onSelect(row) : undefined}>
                          <TableCell>{row.promotion_name || row.promo_id}</TableCell>
                          <TableCell>{row.sku_title || row.sku_id}</TableCell>
                          <TableCell>{row.segment}</TableCell>
                          <TableCell>{row.term_duration}</TableCell>
                          <TableCell align="right">{row.microsoft_promo_pct}%</TableCell>
                          <TableCell>{formatSeatBand(row.min_quantity, row.max_quantity)}</TableCell>
                          <TableCell>{formatDate(row.start_date)}</TableCell>
                          <TableCell>{row.end_date ? formatDate(row.end_date) : "No end"}</TableCell>
                          <TableCell>{row.restrictions.length ? row.restrictions.join("; ") : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>}
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions} component="div" count={data.total}
              rowsPerPage={rowsPerPage} page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={event => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </DialogContent>
      {/* Bottom-left, out of the toolbar's way - the toolbar was already tight for space
          (search + two filters + the Last checked/Refresh group), and this button doesn't need to
          sit next to them to make sense. */}
      {customerGuid && <DialogActions className="promotions-catalog-actions">
        {/* Microsoft has no link to a specific promotion - this customer's own Partner Center
            catalog page is the closest equivalent, and the one place staff can confirm exactly
            what this customer is actually eligible for before committing to a number in a quote. */}
        <Tooltip title="Open this customer's Partner Center catalog in a new tab.">
          <Button size="small" variant="outlined" endIcon={<OpenInNewIcon fontSize="small" />}
            href={partnerCenterCatalogUrl(customerGuid)} target="_blank" rel="noopener noreferrer">
            Partner Center
          </Button>
        </Tooltip>
      </DialogActions>}
    </Dialog>
  );
};

export default PromotionsCatalogDialog;
